<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { ref } from 'vue'
import {
  uploadInternationalPaymentReceiptWithoutChangeStatusAction,
  uploadIranianPaymentReceiptWithoutChangeStatusAction,
  uploadSettlementReceiptWithoutChangeStatusAction,
} from '@/api/functions/transactions/transaction-receipt-upload.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  label: {
    type: String,
  },
  img: { type: Array as () => string[], default: () => [] },
  uuid: { type: String, required: true },
  type: { type: String as () => 'iranian' | 'international' | 'settlement', required: true },
  disabled: { type: Boolean, default: false },
})

interface UploadedFile {
  name: string
  size: string
  url?: string
  file?: File
}

const emit = defineEmits(['confirmUpload'])

const toast = useToast()
const files = ref<UploadedFile[]>([])
const fileInput = ref<HTMLInputElement | null>(null)

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const selectedFiles = Array.from(target.files)
    const newFiles = selectedFiles.map((f) => ({
      name: f.name,
      size: (f.size / 1024).toFixed(2) + ' KB',
      url: URL.createObjectURL(f),
      file: f,
    }))

    files.value = [...files.value, ...newFiles]
    target.value = ''
  }
}

const queryClient = useQueryClient()

const uploadMutation = useMutationApi({
  mutationFn: async () => {
    if (!files.value.length) {
      throw new Error('هیچ فایلی انتخاب نشده است')
    }

    const images = files.value.map((f) => f.file!)

    if (props.type === 'iranian') {
      return uploadIranianPaymentReceiptWithoutChangeStatusAction(props.uuid, { images })
    }

    if (props.type === 'international') {
      return uploadInternationalPaymentReceiptWithoutChangeStatusAction(props.uuid, { images })
    }

    if (props.type === 'settlement') {
      return uploadSettlementReceiptWithoutChangeStatusAction(props.uuid, { images })
    }
  },

  onSuccess: () => {
    toast.success('فایل‌ها با موفقیت آپلود شدند')
    files.value = []
    queryClient.invalidateQueries({ queryKey: ['transactionDetails', props.uuid] })
    emit('confirmUpload')
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err), 'خطا در آپلود فایل')
  },
})

const removeFile = (index: number) => {
  files.value.splice(index, 1)
}

const showImageModal = ref(false)
const selectedImage = ref<string | null>()

const openImageInNewTab = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<template>
    <div class="w-full flex flex-col gap-4 p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
        <div class="grid lg:grid-cols-4 gap-6">
            <!-- images -->
            <div v-for="(file, i) in files" :key="i"
                class="flex flex-col rounded-xl h-[292.46px] shadow cursor-pointer "
                @click="selectedImage = file.url; showImageModal = true">
                <img :src="file.url" class="h-[150px] object-cover rounded-t-xl  hover:scale-105 duration-300" />
                <div class="h-1/2 flex flex-col items-center p-4 text-[#00000099] font-medium">
                    <span class="text-[12.25px] mb-2 truncate w-full text-center">{{ file.name }}</span>
                    <span class="text-[#888] text-[12.6px]">{{ file.size }}</span>
                    <button
                        class="flex items-center justify-center cursor-pointer mt-4 w-12 h-12 rounded-full hover:bg-[#fff2f2]"
                        @click.stop="removeFile(i)">
                        <img src="/svg/delete-3-svgrepo-com.svg" class="w-4" />
                    </button>
                </div>
            </div>


            <!-- input -->
            <label :class="[
                'flex flex-col items-center justify-center shadow-md shadow-[#0000001f] h-[150px] rounded-sm border-2 border-dashed text-[#757575] border-[#aaa]',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ]">
                <span>افزودن رسید</span>
                <span class="text-[50px] h-12 font-light">+</span>
                <input type="file" :disabled="disabled" multiple class="hidden" ref="fileInput"
                    @change="handleFileChange" />
            </label>
        </div>

        <!-- button -->
        <div class="flex items-center gap-4 my-4">
            <Button @click="uploadMutation.mutate()" :disabled="disabled" :isAdmin="true"
                class="w-full lg:w-fit px-6! rounded-lg h-12! py-0 text-base">
                <span class="text-nowrap flex items-center justify-center gap-2">
                    <img src="/svg/link-alt-svgrepo-white.svg" width="20" height="20" class="-rotate-45" />
                    بارگذاری رسید {{ label }}
                </span>
            </Button>
        </div>

        <!-- preview images section -->
        <div v-if="props.img.length" class="w-full flex flex-col gap-4 border-t pt-4">

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div v-for="(image, index) in props.img" :key="index" class="flex flex-col items-center">

                    <img :src="image"
                        class="w-full h-[150px] object-cover cursor-pointer rounded-lg hover:scale-105 duration-300"
                        @click="selectedImage = image; showImageModal = true" />

                    <Button @click.stop="openImageInNewTab(image)" :isAdmin="true"
                        class="w-fit px-5! rounded-lg h-10! py-0 text-[13px] mt-2">
                        دانلود
                    </Button>
                </div>
            </div>
        </div>

        <!--  full-screen image modal -->
        <Modal :model-value="showImageModal" @update:model-value="showImageModal = $event" width="90vw"
            background="rgba(0,0,0,0.8)" :closable="false" title="">
            <div class="flex justify-center items-center min-h-[90vh] ">
                <img :src="selectedImage ?? ''"
                    class=" w-fit h-full bg-cover  transition-all cursor-pointer duration-300 " />
            </div>
        </Modal>
    </div>
</template>
